import { useEffect } from 'react'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useHeroReveal } from '@gjl/motion/css'

const industries = [
  {
    title: 'Classical Music Festivals & Summer Institutes',
    subtitle: 'Student programs, professional festivals, and artist residencies',
    description:
      'Summer training programs, chamber music festivals, early music festivals, contemporary music festivals, specialized instrument programs, conservatory-affiliated institutes',
    items: [
      'Modern festival websites with an editor for easy updates',
      'Application and enrollment funnels with payment processing',
      'Automated email sequences for applicants and attendees',
      'Faculty bios, program schedules, and concert calendars',
      'Analytics and enrollment tracking',
    ],
    examples: ['Chroma International Music Festival', 'Chamber music institutes', 'Youth orchestras'],
  },
  {
    title: 'Symphony Orchestras & Chamber Ensembles',
    subtitle: 'Professional orchestras, chamber groups, and period instrument ensembles',
    description:
      'Regional orchestras, professional chamber music organizations, baroque ensembles, contemporary music groups, period instrument orchestras',
    items: [
      'Concert season websites with integrated ticketing',
      'Donor management and fundraising campaign support',
      'Concert program notes and artist biographies',
      'Email marketing for subscribers and single-ticket buyers',
      'Media galleries for recordings and performance videos',
    ],
    examples: ['Regional orchestras', 'Chamber music societies', 'Baroque orchestras', 'Contemporary ensembles'],
  },
  {
    title: 'Opera Companies & Music Theater',
    subtitle: 'Opera companies, early opera ensembles, and musical theater organizations',
    description:
      'Regional opera companies, contemporary opera ensembles, baroque opera groups, music theater companies, summer opera festivals',
    items: [
      'High-impact websites showcasing productions and artists',
      'Season subscription and single-ticket sales integration',
      'Production calendars with cast and creative team information',
      'Multimedia galleries for trailers, photos, and recordings',
      'Donor and patron management systems',
    ],
    examples: ['Regional opera companies', 'Contemporary opera ensembles', 'Summer opera programs'],
  },
  {
    title: 'Choral Organizations & Sacred Music Programs',
    subtitle: 'Community choruses, church music departments, and vocal ensembles',
    description:
      'Community choruses, professional chamber choirs, church music programs, cathedral choral foundations, sacred music concert series, early music vocal ensembles',
    items: [
      'Concert series websites with event calendars',
      'Ticketing for concerts and fundraising events',
      'Member directories and rehearsal schedules',
      'Donation systems for program support',
      'Media libraries for recordings and performance archives',
    ],
    examples: ['Community choruses', 'Cathedral music programs', 'Chamber choirs', 'Sacred music series'],
  },
  {
    title: 'Conservatories & Music Education Organizations',
    subtitle: 'Degree programs, pre-college divisions, and community music schools',
    description:
      'Conservatory pre-college programs, community music schools, continuing education programs, youth orchestras, music education nonprofits',
    items: [
      'Admissions funnels and application systems',
      'Student and parent portals for program information',
      'Program and course catalogs with registration',
      'CRM integration with institutional systems',
      'Compliance, accessibility, and security features',
    ],
    examples: ['Pre-college programs', 'Community music schools', 'Youth orchestra programs'],
  },
]

export default function WhoWeHelpPage() {
  useEffect(() => {
    document.title = 'Who We Help | Fugue Lab'
  }, [])

  // Hero entrance only; the per-card stagger and hover lifts went with the cards.
  const heroRef = useHeroReveal<HTMLElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12 md:py-24">
      <section ref={heroRef} className="space-y-6 text-center">
        <h1 className="text-fluid-h1">Who We Help</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          Fugue Lab specializes in digital strategy for classical music organizations. We understand the unique
          challenges of festivals, orchestras, chamber ensembles, opera companies, choral organizations, sacred music
          programs, and music education institutions.
        </p>
      </section>

      {/* Five organization types as a typographic list, not five gradient icon
          cards: the name carries each row in the display face, the detail sits
          beside it on a 5/7 split. Rows separate by space alone. */}
      <section aria-label="Organizations we work with" className="space-y-20 md:space-y-28">
        {industries.map(({ title, subtitle, description, items, examples }) => (
          <article key={title} className="grid gap-6 md:grid-cols-12 md:gap-10">
            <div className="space-y-3 md:col-span-5">
              <h2 className="text-fluid-h3 leading-[1.1]">{title}</h2>
              <p className="text-muted-foreground leading-relaxed">{subtitle}</p>
            </div>
            <div className="space-y-6 md:col-span-7">
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed">{description}</p>
              <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {items.map((item) => (
                  <li key={item} className="leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground text-sm leading-relaxed">
                <span className="text-foreground font-semibold">For example:</span> {examples.join(', ')}
              </p>
            </div>
          </article>
        ))}
      </section>

      <section aria-labelledby="fit" className="grid gap-6 md:grid-cols-12 md:gap-10">
        <h2 id="fit" className="text-fluid-h3 md:col-span-5">
          Not Sure If We&apos;re a Fit?
        </h2>
        <div className="space-y-6 md:col-span-7">
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed">
            If you&apos;re running a classical music organization, whether a festival, chamber ensemble, opera company,
            chorus, sacred music program, or music school, and need help with your website, ticketing systems, or
            digital strategy, we&apos;d love to talk.
          </p>
          <a
            href="/book"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary focus-visible:outline-primary inline-block font-medium underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
          >
            Book a Free Call
          </a>
        </div>
      </section>

      <FugueContactForm />
    </div>
  )
}
